import type { PaginationBody, PaginationParams } from '../api/types/api.types';

export interface TableCacheEntry<T = unknown> {
    body: T;
    pagination?: PaginationBody;
    timestamp: number;
}

class TableCacheServiceClass {
    private readonly Store = new Map<string, TableCacheEntry>();
    /** Single rows by table and primary: what a search can show without asking the API. */
    private readonly Rows = new Map<string, Map<number | string, unknown>>();

    private buildKey(tableKey: string, params?: PaginationParams): string {
        const page = params?.page ?? 1;
        const perPage = params?.per_page ?? 100;
        const sort = params?.sort ?? '';
        const dir = params?.dir ?? '';

        return `${tableKey}:${String(page)}:${String(perPage)}:${sort}:${dir}`;
    }

    /**
     * Returns cached entry for table and query parameters if available.
     */
    public get<T>(tableKey: string, params?: PaginationParams): TableCacheEntry<T> | undefined {
        const key = this.buildKey(tableKey, params);
        const entry = this.Store.get(key);
        if (!entry) {
            return undefined;
        }

        return entry as TableCacheEntry<T>;
    }

    /**
     * Caches response body and optional pagination for table and query parameters.
     */
    public set(tableKey: string, body: unknown, params?: PaginationParams, pagination?: PaginationBody): void {
        const key = this.buildKey(tableKey, params);
        this.Store.set(key, {
            body,
            pagination,
            timestamp: Date.now(),
        });
    }

    /**
     * Returns the cached rows of a table in the order of `primaries`; those not cached are left out.
     */
    public listRows<T>(tableKey: string, primaries: (number | string)[]): T[] {
        const Table = this.Rows.get(tableKey);

        return primaries.filter((primary): boolean => Table?.has(primary) ?? false).map((primary): T => Table?.get(primary) as T);
    }

    public hasRow(tableKey: string, primary: number | string): boolean {
        return this.Rows.get(tableKey)?.has(primary) ?? false;
    }

    /**
     * Caches single rows of a table, each under its primary.
     */
    public setRows<T>(tableKey: string, rows: T[], getPrimary: (row: T) => number | string): void {
        const Table = this.Rows.get(tableKey) ?? new Map<number | string, unknown>();
        for (const Row of rows) {
            Table.set(getPrimary(Row), Row);
        }
        this.Rows.set(tableKey, Table);
    }

    /**
     * Clears all cache entries or entries matching a given table key.
     */
    public clear(tableKey?: string): void {
        if (!tableKey) {
            this.Store.clear();
            this.Rows.clear();
            return;
        }

        this.Rows.delete(tableKey);
        const prefix = `${tableKey}:`;
        for (const k of this.Store.keys()) {
            if (k === tableKey || k.startsWith(prefix)) {
                this.Store.delete(k);
            }
        }
    }
}

export const TableCacheService = new TableCacheServiceClass();
